// Service hook (TS-2.5.01, Resource Accessor layer) - the only place this feature's list screen
// calls portfolioDataApi.listProjects. Mirrors useRoadmap's shape (fetch on mount and whenever the
// caller's memoized query changes, refetch on any push-channel invalidation for this entity type,
// surface a typed ApiError rather than throwing).
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import { EntityTypes } from "../../../api/generated/contracts";
import type { PagedResult, ProjectSummary } from "../../../api/generated/contracts";
import type { ListProjectsParams } from "../../../api/portfolioData";
import { portfolioDataApi } from "../../../api/portfolioData";
import { useInvalidation } from "../../../realtime/useInvalidation";

export interface UseProjectsResult {
  readonly page: PagedResult<ProjectSummary> | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
  readonly refresh: () => void;
}

/**
 * `params` must be caller-memoized (e.g. via `useMemo`), the same contract `useRoadmap` places on
 * its `filter` argument - this effect only re-fires when the reference actually changes.
 */
export function useProjects(params: ListProjectsParams): UseProjectsResult {
  const [page, setPage] = useState<PagedResult<ProjectSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    void portfolioDataApi
      .listProjects(params)
      .then((next) => {
        setPage(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The project list could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(refresh, [refresh]);
  useInvalidation(refresh, EntityTypes.Project);

  return { page, loading, error, refresh };
}
